// Editor de plantillas de escenas para la cartelería TV (POS → Configuración → TV · Plantillas).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, LayoutTemplate, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Campo, Modal, Switch, fieldCls } from './shared';

const rpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body)).data;

interface Escena { tipo: string; titulo?: string; duracion?: number; n_items?: number; categoria_id?: string; texto?: string; medio_id?: string }
interface Plantilla { id: string; nombre: string; escenas: Escena[]; es_activo: boolean }
interface Categoria { id: string; nombre: string }
interface Medio { id: string; nombre: string }

const TIPOS: Record<string, string> = {
  mas_vendidos: 'Más vendidos', categoria: 'Por categoría', promociones: 'Promociones',
  banner: 'Banner (imagen)', qr: 'Código QR', mensaje: 'Mensaje',
};
const TIPOS_PRODUCTO = ['mas_vendidos', 'categoria', 'promociones'];
const escenaNueva = (): Escena => ({ tipo: 'mas_vendidos', titulo: '', duracion: 15, n_items: 12 });
const vacio = { id: '', nombre: '', es_activo: true, escenas: [escenaNueva()] as Escena[] };
type Form = typeof vacio;

export function TvPlantillas() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching, error } = useQuery<Plantilla[]>({ queryKey: ['tvPlantillas'], queryFn: () => rpc<Plantilla[]>('tv_plantillas') });
  const { data: categorias = [] } = useQuery<Categoria[]>({
    queryKey: ['invCategorias'],
    queryFn: async () => (await apiClient.get<Categoria[]>('/rest/inventario.categorias?select=id,nombre&deleted_at=is.null&order=nombre')).data,
  });
  const { data: medios = [] } = useQuery<Medio[]>({ queryKey: ['tvMedios'], queryFn: () => rpc<Medio[]>('tv_medios') });
  const [form, setForm] = useState<Form | null>(null);

  const guardar = useMutation({
    mutationFn: (f: Form) => rpc('tv_plantilla_guardar', { _data: { id: f.id || undefined, nombre: f.nombre, es_activo: f.es_activo, escenas: f.escenas } }),
    onSuccess: () => { toast.success('Plantilla guardada'); setForm(null); void qc.invalidateQueries({ queryKey: ['tvPlantillas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => rpc('tv_plantilla_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Plantilla eliminada'); void qc.invalidateQueries({ queryKey: ['tvPlantillas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const setEsc = (i: number, patch: Partial<Escena>) => setForm((f) => f && ({ ...f, escenas: f.escenas.map((e, j) => (j === i ? { ...e, ...patch } : e)) }));
  const addEsc = () => setForm((f) => f && ({ ...f, escenas: [...f.escenas, escenaNueva()] }));
  const delEsc = (i: number) => setForm((f) => f && ({ ...f, escenas: f.escenas.filter((_, j) => j !== i) }));
  const moveEsc = (i: number, dir: -1 | 1) => setForm((f) => {
    if (!f) return f; const j = i + dir; if (j < 0 || j >= f.escenas.length) return f;
    const arr = [...f.escenas]; [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...f, escenas: arr };
  });

  if (error) return <div className="p-6 text-sm text-urgente">{getErrorMessage(error)}</div>;

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><LayoutTemplate className="w-5 h-5 text-primary" /> TV · Plantillas</h1>
          <p className="text-sm text-muted-foreground">Define las escenas que rota la cartelería. Asigna una plantilla a cada dispositivo en TV / Cartelería.</p>
        </div>
        <Button onClick={() => setForm({ ...vacio, escenas: [escenaNueva()] })}><Plus className="w-4 h-4 mr-1" /> Nueva plantilla</Button>
      </div>

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Escenas</th><th className="py-2 pr-3">Activa</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">Sin plantillas</td></tr>}
              {rows.map((p) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{p.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{(p.escenas ?? []).map((e) => TIPOS[e.tipo] ?? e.tipo).join(' · ') || '—'}</td>
                  <td className="py-2 pr-3">{p.es_activo ? 'Sí' : 'No'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" aria-label="Editar"
                      onClick={() => setForm({ id: p.id, nombre: p.nombre, es_activo: p.es_activo, escenas: (p.escenas ?? []).length ? p.escenas : [escenaNueva()] })}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" aria-label="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${p.nombre}"?`, { peligro: true })) eliminar.mutate(p.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {form && (
        <Modal title={form.id ? 'Editar plantilla' : 'Nueva plantilla'} onClose={() => setForm(null)} wide>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Nombre *" className="col-span-2"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
              <div className="flex items-end pb-2"><Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activa" /></div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-muted-foreground">Escenas (en orden de reproducción)</span>
                <Button size="sm" variant="outline" onClick={addEsc}><Plus className="w-4 h-4 mr-1" /> Escena</Button>
              </div>
              {form.escenas.map((e, i) => (
                <div key={i} className="rounded-md border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-muted-foreground w-6">{i + 1}.</span>
                    <select className={`${fieldCls} w-44`} value={e.tipo} onChange={(ev) => setEsc(i, { tipo: ev.target.value })}>
                      {Object.entries(TIPOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <input className={`${fieldCls} flex-1`} placeholder="Título (opcional)" value={e.titulo ?? ''} onChange={(ev) => setEsc(i, { titulo: ev.target.value })} />
                    <span className="ml-auto flex items-center gap-1">
                      <button className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={i === 0} onClick={() => moveEsc(i, -1)} title="Subir" aria-label="Subir"><ArrowUp className="w-4 h-4" /></button>
                      <button className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={i === form.escenas.length - 1} onClick={() => moveEsc(i, 1)} title="Bajar" aria-label="Bajar"><ArrowDown className="w-4 h-4" /></button>
                      <button className="text-red-500" onClick={() => delEsc(i)} title="Quitar" aria-label="Quitar"><Trash2 className="w-4 h-4" /></button>
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pl-8 text-sm">
                    <label className="flex items-center gap-1">Duración <input type="number" min={4} className={`${fieldCls} w-20`} value={e.duracion ?? 15} onChange={(ev) => setEsc(i, { duracion: Number(ev.target.value) })} /> s</label>
                    {TIPOS_PRODUCTO.includes(e.tipo) && (
                      <label className="flex items-center gap-1">Productos <input type="number" min={1} max={24} className={`${fieldCls} w-20`} value={e.n_items ?? 12} onChange={(ev) => setEsc(i, { n_items: Number(ev.target.value) })} /></label>
                    )}
                    {e.tipo === 'categoria' && (
                      <select className={`${fieldCls} w-56`} value={e.categoria_id ?? ''} onChange={(ev) => setEsc(i, { categoria_id: ev.target.value })}>
                        <option value="">— Categoría —</option>
                        {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                      </select>
                    )}
                    {e.tipo === 'banner' && (
                      <>
                        <select className={`${fieldCls} w-56`} value={e.medio_id ?? ''} onChange={(ev) => setEsc(i, { medio_id: ev.target.value })}>
                          <option value="">— Imagen (biblioteca) —</option>
                          {medios.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                        </select>
                        <input className={`${fieldCls} flex-1`} placeholder="Texto sobre la imagen (opcional)" value={e.texto ?? ''} onChange={(ev) => setEsc(i, { texto: ev.target.value })} />
                      </>
                    )}
                    {e.tipo === 'qr' && (
                      <input className={`${fieldCls} flex-1`} placeholder="Contenido / URL del QR (ej. https://…)" value={e.texto ?? ''} onChange={(ev) => setEsc(i, { texto: ev.target.value })} />
                    )}
                    {e.tipo === 'mensaje' && (
                      <input className={`${fieldCls} flex-1`} placeholder="Texto del mensaje" value={e.texto ?? ''} onChange={(ev) => setEsc(i, { texto: ev.target.value })} />
                    )}
                  </div>
                </div>
              ))}
              {form.escenas.length === 0 && <p className="text-sm text-pendiente">Agrega al menos una escena.</p>}
            </div>

            <p className="text-xs text-muted-foreground">Nota: la TV solo muestra productos no sensibles (excluye medicamentos). El modo cliente (sugeridos por venta) se activa solo si el dispositivo tiene una caja asignada.</p>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim() || form.escenas.length === 0} onClick={() => guardar.mutate(form)}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
